/**
 * appAccess.js (OPUS-902) — soft app-entry gate for AeDrive.
 *
 * Reads the access token's permissions[] (minted by user-management) and checks
 * apps.aedrive.access. SOFT by default: only logs a would-deny so existing
 * operators (incl. the live-match copilot flow) are never locked out while the
 * role->permission map is finalised. Flip ENFORCE to hard-block.
 */
const APP_PERMISSION = "apps.aedrive.access";
const QUICKLINK_PERMISSION = "quicklinks.aedrive.view";
export const ENFORCE_APP_ACCESS = false; // soft: log only, never block

export const decodePermissions = (token) => {
  try {
    if (!token || typeof token !== "string") return [];
    const part = token.split(".")[1];
    if (!part) return [];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return Array.isArray(payload.permissions) ? payload.permissions : [];
  } catch (_) {
    return [];
  }
};

/**
 * True if this session is a super admin (OPUS-1505).
 *
 * Reads the JWT's permissions[] claim — the same source hasAedriveAccess and
 * rendererLock.js already use — rather than `session.role`. Three reasons:
 *
 *   - session.role goes STALE. auth.service.js spreads the previous session on
 *     every hourly refresh and never re-derives it.
 *   - it is legitimately nullable: user-management's handoff returns
 *     `user.role?.name || null`.
 *   - the Aether -> Drive deep link carries no session payload at all, so role
 *     may be absent entirely.
 *
 * '*' is @ae/auth's ALL_PERMISSIONS wildcard, held by super_admin.
 *
 * Fails CLOSED: a missing or unparseable token yields [], so the caller hides
 * the thing it gates rather than exposing it.
 */
export const isSuperAdmin = (session) =>
  decodePermissions(session?.accessKey || session?.accessToken || null).includes("*");

/** True if the session may access AeDrive. super_admin ('*') always passes. */
export const hasAedriveAccess = (session) => {
  const token = session?.accessKey || session?.accessToken || null;
  const perms = decodePermissions(token);
  const allowed =
    perms.includes("*") ||
    perms.includes(APP_PERMISSION) ||
    perms.includes(QUICKLINK_PERMISSION);
  if (!allowed) {
    console.warn(
      `[RBAC shadow] would-deny AeDrive entry: missing ${APP_PERMISSION} (token perms: ${perms.length})`
    );
  }
  return allowed;
};
